import { FC } from "react";
import { Button, Typography, Card, CardContent, CardHeader, Checkbox } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import type { TestCase } from "../types";

interface TestCaseSelectorProps {
  testCases: TestCase[];
  selectedTestCases: string[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const TestCaseSelector: FC<TestCaseSelectorProps> = ({
  testCases,
  selectedTestCases,
  onToggle,
  onSelectAll,
  onDeselectAll,
}) => {
  return (
    <Card>
      <CardHeader>
        <Stack direction="row" justify="between" align="center">
          <Typography size="regular" weight="medium">
            Test Cases ({selectedTestCases.length}/{testCases.length})
          </Typography>
          <Stack direction="row" gap="xs">
            <Button onClick={onSelectAll} variant="ghost" size="sm" className="al-text-xs">
              Select All
            </Button>
            <Button onClick={onDeselectAll} variant="ghost" size="sm" className="al-text-xs">
              Clear
            </Button>
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent className="al-max-h-96 al-overflow-y-auto">
        {testCases.length === 0 ? (
          <div className="al-text-center al-py-8 al-text-sm al-text-gray-500">
            No test cases available. Create test cases in the Test Cases tab.
          </div>
        ) : (
          <div className="al-space-y-2">
            {testCases.map(testCase => (
              <div
                key={testCase.id}
                role="button"
                tabIndex={0}
                className={`al-p-3 al-border al-rounded-md al-cursor-pointer al-transition-colors ${
                  selectedTestCases.includes(testCase.id)
                    ? "al-bg-green-50 al-border-green-300"
                    : "al-bg-white al-border-gray-200 hover:al-border-gray-300"
                }`}
                onClick={() => onToggle(testCase.id)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle(testCase.id);
                  }
                }}
              >
                <Stack direction="row" align="start" gap="sm">
                  <Checkbox
                    checked={selectedTestCases.includes(testCase.id)}
                    onCheckedChange={() => onToggle(testCase.id)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    className="al-mt-0.5"
                  />
                  <div className="al-flex-1">
                    <Typography size="sm" weight="medium" className="al-mb-1">
                      {testCase.name}
                    </Typography>
                    {testCase.description && (
                      <Typography size="xs" className="al-text-gray-600 al-line-clamp-2">
                        {testCase.description}
                      </Typography>
                    )}
                    <Typography size="xs" className="al-text-gray-500 al-mt-1">
                      {testCase.turns.length} turns • {Object.keys(testCase.criteria).length}{" "}
                      criteria
                    </Typography>
                  </div>
                </Stack>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
